import apiService from "@/services/apiService";
import { debugLog } from "@/utils/debugUtils";
import { preprocessImage } from "@/utils/imageOptimizationUtility";

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache
const REFRESH_INTERVAL = 1 * 60 * 1000; // Refresh every 10 minutes
let refreshTimer = null;

function logPropertyListings(data, message = "") {
  if (data && data.length > 0) {
    // Count by category
    const categoryCount = {};
    data.forEach((item) => {
      const catId = item.category_id;
      categoryCount[catId] = (categoryCount[catId] || 0) + 1;
    });
  } else {
  }
}

function validatePropertyTypeFilter(propertyType) {
  if (!propertyType) return null;

  if (typeof propertyType === "object") {
    return {
      id: propertyType.id != null ? Number(propertyType.id) : null,
      name: propertyType.name || propertyType.title || null,
    };
  }

  // Handle primitive values (numbers or strings)
  return {
    id: Number(propertyType),
    name: null,
  };
}

/**
 * Normalize property listing data for consistent structure
 * @param {Object} listing - Raw property listing data from API
 * @return {Object} - Normalized property listing with consistent types
 */
function normalizePropertyListing(listing) {
  if (!listing || typeof listing !== "object") return {};

  const property = listing.property || {};
  const subcategory = listing.sub_category || listing.subCategory || null;
  const category = listing.category || {};
  const user = listing.user || {};
  const images = Array.isArray(listing.image_urls)
    ? listing.image_urls.map(
        (url) => preprocessImage(url, { responsive: true }).thumbnailUrl
      )
    : Array.isArray(listing.images)
    ? listing.images.map(
        (url) => preprocessImage(url, { responsive: true }).thumbnailUrl
      )
    : [];

  const normalized = {
    id: listing.id,
    title: listing.title || "",
    description: listing.description || "",
    tags: Array.isArray(listing.tags)
      ? listing.tags
      : listing.tags
      ? [listing.tags]
      : [],
    price: parseFloat(listing.price) || 0,
    isFeatured: !!listing.isFeatured,
    isFurnished: !!listing.is_furnished || !!property.isFurnished,
    status: listing.status || 0,
    verified: !!listing.verified,
    city_name: listing.city_name || "",
    currency: listing.currency || "USD",
    google_place_id: listing.google_place_id || null,
    location: {
      lat: parseFloat(listing.selectedLat) || null,
      lng: parseFloat(listing.selectedLng) || null,
    },
    created_at: listing.created_at || null,
    updated_at: listing.updated_at || null,
    category: {
      id: category.id || listing.category_id || null,
      name: category.name || "",
    },
    subcategory: subcategory
      ? {
          id: subcategory.id || listing.sub_category_id || null,
          name: subcategory.name || subcategory.title || "",
          slug: subcategory.slug || "",
        }
      : null,

    user: {
      id: user.id || listing.user_id || null,
      fullName: user.fullName || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      created_at: user.created_at || null,
    },

    property: {
      id: property.id || null,
      bedrooms: parseInt(property.bedrooms) || null,
      bathrooms: parseFloat(property.bathrooms) || null,
      size: parseFloat(property.size) || null,
      isFurnished: !!property.isFurnished,
      rentPaid: property.rentPaid || null,
      propertyType: property.propertyType ?? property.property_type ?? null,
      propertyTypeId:
        property.propertyTypeId ??
        property.property_type_id ??
        property.group_id ??
        listing.group_id ??
        listing.property_type_group_id ??
        null,
      minimumContractPeriod: property.minimumContractPeriod || null,
      pricePerYear: parseFloat(property.pricePerYear) || null,
      occupancyStatus: property.occupancyStatus || null,
      amenities: normalizeAmenities(
        property.amenities_list || property.amenities || []
      ),
    },
    group_id: listing.group_id ?? listing.property_type_group_id ?? null,
    property_type_id: listing.property_type_id ?? null,
    images,
    payment_phone_number: listing.payment_phone_number || null,
  };

  return normalized;
}

export default {
  namespaced: true,
  state: {
    propertyListings: [],
    filters: {
      query: "",
      location: null,
      city: [],
      priceRange: { min: null, max: null },
      bedrooms: { min: null, max: null },
      bathrooms: { min: null, max: null },
      size: { min: null, max: null },
      amenities: [],
      propertyType: null,
      isFurnished: null,
      subcategory: null,
      verified: false,
      category: null,
    },
    verified: false,
    loading: false,
    error: null,
    selectedPropertyListing: null,
    similarProperties: [],
    lastFetch: 0,
    cacheValid: true,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      perPage: 20,
      total: 0,
    },
  },

  mutations: {
    SET_PROPERTY_LISTINGS(state, listings) {
      state.propertyListings = listings;
      state.lastFetch = Date.now();
      state.cacheValid = true;
    },

    // SET_FILTERS(state, filters) {
    //   // Deep clone the filters to handle Vue reactive Proxy objects properly
    //   const cleanFilters = {};

    //   Object.keys(filters).forEach((key) => {
    //     // Skip undefined values to avoid overwriting existing filters with undefined
    //     if (filters[key] === undefined) {
    //       return;
    //     }

    //     const value = filters[key];

    //     if (Array.isArray(value)) {
    //       // Deep clone arrays and filter out null/undefined
    //       cleanFilters[key] = value
    //         .filter((item) => item != null)
    //         .map((item) => {
    //           if (typeof item === "object" && item !== null) {
    //             return JSON.parse(JSON.stringify(item));
    //           }
    //           return item;
    //         });
    //     } else if (typeof value === "object" && value !== null) {
    //       // Deep clone objects, but skip empty objects
    //       const cloned = JSON.parse(JSON.stringify(value));

    //       // Check if object has any meaningful properties (not just {})
    //       const hasMeaningfulData = Object.values(cloned).some(
    //         (val) => val !== null && val !== undefined && val !== ""
    //       );

    //       if (hasMeaningfulData) {
    //         cleanFilters[key] = cloned;
    //       }
    //     } else if (value !== null && value !== undefined && value !== "") {
    //       // Handle primitives, but skip null, undefined, and empty strings
    //       cleanFilters[key] = value;
    //     }
    //   });

    //   // ✅ ENHANCED: Handle property type filters with validation
    //   const validatePropertyType = (pt) => {
    //     if (!pt) return null;
    //     if (typeof pt === "object") {
    //       return {
    //         id: pt.id != null ? Number(pt.id) : null,
    //         name: pt.name || pt.title || null,
    //       };
    //     }
    //     return {
    //       id: Number(pt),
    //       name: null,
    //     };
    //   };

    //   // Process propertyType and propertyTypeGroup together
    //   const propertyTypeFromFilter =
    //     filters.propertyType || filters.propertyTypeGroup;
    //   if (propertyTypeFromFilter) {
    //     const validated = validatePropertyType(propertyTypeFromFilter);

    //     // Set both for consistency
    //     cleanFilters.propertyType = validated;
    //     cleanFilters.propertyTypeGroup = validated;

    //     console.log("[STORE] Setting property type filter:", validated);
    //   } else if (
    //     filters.propertyType === null ||
    //     filters.propertyTypeGroup === null
    //   ) {
    //     // Explicitly clear if null is passed
    //     cleanFilters.propertyType = null;
    //     cleanFilters.propertyTypeGroup = null;
    //   }

    //   // ✅ FIXED: Special handling for isFurnished - preserve existing if not explicitly set
    //   if (filters.hasOwnProperty("isFurnished")) {
    //     cleanFilters.isFurnished = filters.isFurnished;
    //   } else {
    //     // Preserve existing isFurnished value if not explicitly set in new filters
    //     cleanFilters.isFurnished = state.filters.isFurnished;
    //   }

    //   // ✅ ENHANCED: Handle city filter properly (can be array or single object)
    //   if (filters.hasOwnProperty("city")) {
    //     if (Array.isArray(filters.city)) {
    //       cleanFilters.city = filters.city.filter((city) => city != null);
    //     } else if (filters.city && typeof filters.city === "object") {
    //       cleanFilters.city = [filters.city];
    //     } else {
    //       cleanFilters.city = [];
    //     }
    //   }

    //   // ✅ ENHANCED: Handle range filters with validation
    //   const validateRange = (range) => {
    //     if (!range || typeof range !== "object")
    //       return { min: null, max: null };

    //     const min = range.min != null ? Number(range.min) : null;
    //     const max = range.max != null ? Number(range.max) : null;

    //     // Validate min <= max
    //     if (min != null && max != null && min > max) {
    //       console.warn("Invalid range: min > max, resetting to min only");
    //       return { min, max: null };
    //     }

    //     return { min, max };
    //   };

    //   ["priceRange", "bedrooms", "bathrooms", "size"].forEach((rangeKey) => {
    //     if (filters.hasOwnProperty(rangeKey)) {
    //       cleanFilters[rangeKey] = validateRange(filters[rangeKey]);
    //     }
    //   });

    //   // ✅ ENHANCED: Merge with existing filters instead of complete replacement
    //   // This preserves filters that weren't explicitly set in the new update
    //   state.filters = {
    //     ...state.filters, // Keep existing filters
    //     ...cleanFilters, // Apply new/updated filters
    //   };

    //   // Debug log the final filter state
    //   console.log("[STORE] Final filters after SET_FILTERS:", {
    //     ...state.filters,
    //     // Don't log entire arrays to avoid console spam
    //     city: state.filters.city?.map((c) => ({ id: c.id, name: c.name })),
    //     amenities: state.filters.amenities?.length,
    //   });
    // },

    //might be deleted in future

    SET_FILTERS(state, filters) {
      // Handle explicit null/undefined values for category-related filters
      const cleanFilters = {};

      Object.keys(filters).forEach((key) => {
        // Explicitly handle null values for category, subcategory, and propertyType
        if (
          (key === "category" ||
            key === "subcategory" ||
            key === "propertyType" ||
            key === "propertyTypeGroup") &&
          (filters[key] === null || filters[key] === undefined)
        ) {
          cleanFilters[key] = null;
          return;
        }

        // Skip undefined values to avoid overwriting existing filters with undefined
        if (filters[key] === undefined) {
          return;
        }

        const value = filters[key];

        if (Array.isArray(value)) {
          // Deep clone arrays and filter out null/undefined
          cleanFilters[key] = value
            .filter((item) => item != null)
            .map((item) => {
              if (typeof item === "object" && item !== null) {
                return JSON.parse(JSON.stringify(item));
              }
              return item;
            });
        } else if (typeof value === "object" && value !== null) {
          // Deep clone objects, but skip empty objects
          const cloned = JSON.parse(JSON.stringify(value));

          // Check if object has any meaningful properties (not just {})
          const hasMeaningfulData = Object.values(cloned).some(
            (val) => val !== null && val !== undefined && val !== ""
          );

          if (hasMeaningfulData) {
            cleanFilters[key] = cloned;
          }
        } else if (value !== null && value !== undefined && value !== "") {
          // Handle primitives, but skip null, undefined, and empty strings
          cleanFilters[key] = value;
        }
      });

      // ✅ CRITICAL FIX: When category is explicitly set to null, also clear related filters
      if (filters.category === null || filters.category === undefined) {
        cleanFilters.subcategory = null;
        cleanFilters.propertyType = null;
        cleanFilters.propertyTypeGroup = null;
      }

      // ✅ CRITICAL FIX: When subcategory is explicitly set to null, clear property type
      if (filters.subcategory === null || filters.subcategory === undefined) {
        cleanFilters.propertyType = null;
        cleanFilters.propertyTypeGroup = null;
      }

      // ✅ ENHANCED: Handle property type filters with validation
      const validatePropertyType = (pt) => {
        if (!pt) return null;
        if (typeof pt === "object") {
          return {
            id: pt.id != null ? Number(pt.id) : null,
            name: pt.name || pt.title || null,
          };
        }
        return {
          id: Number(pt),
          name: null,
        };
      };

      // Process propertyType and propertyTypeGroup together
      const propertyTypeFromFilter =
        filters.propertyType || filters.propertyTypeGroup;
      if (propertyTypeFromFilter) {
        const validated = validatePropertyType(propertyTypeFromFilter);
        cleanFilters.propertyType = validated;
        cleanFilters.propertyTypeGroup = validated;
      } else if (
        filters.propertyType === null ||
        filters.propertyTypeGroup === null
      ) {
        // Explicitly clear if null is passed
        cleanFilters.propertyType = null;
        cleanFilters.propertyTypeGroup = null;
      }

      // ✅ FIXED: Special handling for isFurnished - preserve existing if not explicitly set
      if (filters.hasOwnProperty("isFurnished")) {
        cleanFilters.isFurnished = filters.isFurnished;
      } else {
        // Preserve existing isFurnished value if not explicitly set in new filters
        cleanFilters.isFurnished = state.filters.isFurnished;
      }

      // ✅ ENHANCED: Handle city filter properly (can be array or single object)
      if (filters.hasOwnProperty("city")) {
        if (Array.isArray(filters.city)) {
          cleanFilters.city = filters.city.filter((city) => city != null);
        } else if (filters.city && typeof filters.city === "object") {
          cleanFilters.city = [filters.city];
        } else {
          cleanFilters.city = [];
        }
      }

      // ✅ ENHANCED: Merge with existing filters instead of complete replacement
      // This preserves filters that weren't explicitly set in the new update
      state.filters = {
        ...state.filters, // Keep existing filters
        ...cleanFilters, // Apply new/updated filters
      };

      // Debug log the final filter state
      console.log("[STORE] Final filters after SET_FILTERS:", {
        ...state.filters,
        // Don't log entire arrays to avoid console spam
        city: state.filters.city?.map((c) => ({ id: c.id, name: c.name })),
        amenities: state.filters.amenities?.length,
      });
    },

    SET_VERIFIED_FILTER(state, verified) {
      state.filters.verified = !!verified;
    },
    RESET_FILTERS(state) {
      state.filters = getDefaultFilters();
    },
    SET_SELECTED_PROPERTY(state, property) {
      state.selectedPropertyListing = property
        ? normalizePropertyListing(property)
        : null;
    },
    SET_SIMILAR_PROPERTIES(state, properties) {
      state.similarProperties = Array.isArray(properties)
        ? properties.map((p) => normalizePropertyListing(p))
        : [];
    },
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_CACHE_VALID(state, isValid) {
      state.cacheValid = isValid;
    },
    SET_PAGINATION(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },
    SET_LAST_FETCH(state, timestamp) {
      state.lastFetch = timestamp;
    },
  },

  actions: {
    async initialize({ commit, dispatch, state }) {
      const cacheAge = Date.now() - state.lastFetch;
      const cacheExpired = cacheAge > CACHE_TTL_MS;
      const needsInitialLoad = state.propertyListings.length === 0;

      if (needsInitialLoad || cacheExpired || !state.cacheValid) {
        commit("SET_LOADING", true);
        try {
          const apiResponse = await apiService.get("property-listings");
          const listingsData = apiResponse.data?.original?.data?.data || [];

          const normalizedListings = listingsData.map(normalizePropertyListing);

          commit("SET_PROPERTY_LISTINGS", normalizedListings);
          commit("SET_LAST_FETCH", Date.now());
          commit("SET_CACHE_VALID", true);

          commit("SET_PAGINATION", {
            total: apiResponse.data?.original?.data?.total || 0,
            totalPages: apiResponse.data?.original?.data?.last_page || 1,
            currentPage: apiResponse.data?.original?.data?.current_page || 1,
            perPage: apiResponse.data?.original?.data?.per_page || 20,
          });
        } catch (error) {
          commit("SET_ERROR", "Failed to fetch property listings.");
          commit("SET_CACHE_VALID", false);
        } finally {
          commit("SET_LOADING", false);
        }
      }
    },

    setupPeriodicRefresh({ dispatch }) {
      if (refreshTimer) clearInterval(refreshTimer);
      refreshTimer = setInterval(() => {
        dispatch("refreshPropertyListings");
      }, REFRESH_INTERVAL);
    },

    async refreshPropertyListings({ commit }) {
      try {
        debugLog(
          "PropertyFilterStore: Refreshing property listings",
          {},
          "blue",
          "PropertyFilterStore.js:refreshPropertyListings"
        );

        const response = await apiService.get("property-listings");
        const listings = response.data?.data || [];
        const normalizedListings = listings.map(normalizePropertyListing);

        commit("SET_PROPERTY_LISTINGS", normalizedListings);
      } catch (error) {
        // Error handling
      }
    },

    updateFilters({ commit }, filters) {
      commit("SET_FILTERS", filters);
    },

    setSelectedProperty({ commit }, property) {
      commit("SET_SELECTED_PROPERTY", property);
    },

    resetFilters({ commit }) {
      commit("RESET_FILTERS");
      return Promise.resolve(getDefaultFilters());
    },

    setVerifiedFilter({ commit }, verified) {
      commit("SET_VERIFIED_FILTER", verified);
    },
  },

  getters: {
    filteredPropertyListings:
      (state) =>
      (additionalFilters = {}) => {
        const startTime = performance.now();

        const combinedFilters = {
          ...state.filters,
          ...additionalFilters,
        };

        const filtered = state.propertyListings.filter((item) => {
          return filterPropertyListing(item, combinedFilters);
        });

        const endTime = performance.now();
        const duration = (endTime - startTime).toFixed(2);

        return filtered;
      },

    allPropertyListings: (state) => {
      return state.propertyListings || [];
    },

    currentFilters: (state) => state.filters,

    hasActiveFilters: (state) => {
      return hasAnyActiveFilters(state.filters);
    },

    filterSummary: (state) => {
      return generateFilterSummary(state.filters);
    },
  },
};

function filterPropertyListing(item, filters) {
  if (filters.propertyType || filters.propertyTypeGroup) {
    const pt = filters.propertyType || filters.propertyTypeGroup;

    if (item.id <= 5) {
      // Only log first 5 items to avoid spam
      console.log("[PROPERTY TYPE DEBUG] Comparing:", {
        itemId: item.id,
        itemTitle: item.title,
        filterType: pt,
        itemPropertyTypeId: item.property?.propertyTypeId,
        itemPropertyType: item.property?.propertyType,
        itemGroupId: item.group_id,
        itemPropertyTypeGroupId: item.property?.group_id,
        matches: matchesPropertyType(item, pt),
      });
    }

    const validatedPropertyType = validatePropertyTypeFilter(
      filters.propertyType || filters.propertyTypeGroup
    );

    if (!matchesPropertyType(item, validatedPropertyType)) {
      return false;
    }
  }

  if (filters.keyword) {
    if (!matchesQuery(item, filters.keyword)) {
      return false;
    }
  }

  if (filters.category) {
    if (!matchesCategory(item, filters.category)) {
      return false;
    }
  }

  if (filters.city && filters.city.length > 0) {
    // 🔍 DEBUG: Log city matching
    if (item.id === 1) {
      // Only log for first item
      console.log("🔍 [DEBUG] matchesLocation - City filter:", {
        cityFilter: filters.city,
        itemCityName: item.city_name,
        itemCity: item.city,
        itemPropertyCity: item.property?.city_name,
        matches: matchesLocation(item, filters.city),
      });
    }
    if (!matchesLocation(item, filters.city)) {
      return false;
    }
  }

  if (filters.priceRange?.min || filters.priceRange?.max) {
    if (!matchesPriceRange(item, filters.priceRange)) {
      return false;
    }
  }

  if (filters.bedrooms?.min || filters.bedrooms?.max) {
    if (!matchesBedrooms(item, filters.bedrooms)) {
      return false;
    }
  }

  if (filters.isFurnished !== null && filters.isFurnished !== undefined) {
    if (!matchesFurnished(item, filters.isFurnished)) {
      return false;
    }
  }

  if (filters.subcategory) {
    if (!matchesSubcategory(item, filters.subcategory)) {
      return false;
    }
  }

  if (filters.verified) {
    if (!matchesVerified(item, filters.verified)) {
      return false;
    }
  }

  if (filters.bathrooms?.min || filters.bathrooms?.max) {
    if (!matchesBathrooms(item, filters.bathrooms)) {
      return false;
    }
  }

  if (filters.size?.min || filters.size?.max) {
    if (!matchesSize(item, filters.size)) {
      return false;
    }
  }

  if (filters.amenities && filters.amenities.length > 0) {
    if (!matchesAmenities(item, filters.amenities)) {
      return false;
    }
  }

  // if (filters.propertyType) {
  //   if (!matchesPropertyType(item, filters.propertyType)) {
  //     return false;
  //   }
  // }

  const typeFilter = filters.propertyType ?? filters.propertyTypeGroup ?? null;
  if (typeFilter && !matchesPropertyType(item, typeFilter)) {
    return false;
  }

  return true;
}

function matchesQuery(item, query) {
  if (!query) return true;

  const queryLower = query.toLowerCase();
  const titleMatch = (item.title || "").toLowerCase().includes(queryLower);
  const descriptionMatch = (item.description || "")
    .toLowerCase()
    .includes(queryLower);
  const cityMatch = (item.city_name || "").toLowerCase().includes(queryLower);

  return titleMatch || descriptionMatch || cityMatch;
}

function matchesCategory(item, category) {
  if (!category) return true;

  const categoryId = typeof category === "object" ? category.id : category;
  const itemCategoryId = item.category?.id || item.category_id;
  return itemCategoryId == categoryId;
}

// function matchesLocation(item, cityFilter) {
//   if (!cityFilter || !cityFilter.length) return true;

//   const itemCityName = (
//     item?.city_name ||
//     item?.city?.name ||
//     item?.property?.city_name ||
//     ""
//   ).toLowerCase();

//   const validCityFilter = cityFilter.filter(
//     (city) => city && typeof city === "object" && typeof city.name === "string"
//   );

//   return validCityFilter.some(
//     (city) => city.name.toLowerCase() === itemCityName
//   );
// }

function matchesLocation(item, cityFilter) {
  if (!cityFilter || !cityFilter.length) return true;

  const itemCityName = (
    item?.city_name ||
    item?.city?.name ||
    item?.property?.city_name ||
    ""
  )
    .toLowerCase()
    .trim();

  const validCityFilter = cityFilter.filter(
    (city) => city && typeof city === "object" && typeof city.name === "string"
  );

  const matchFound = validCityFilter.some(
    (city) => city.name.toLowerCase().trim() === itemCityName
  );

  return matchFound;
}

function matchesPriceRange(item, priceRange) {
  if (!priceRange || (!priceRange.min && !priceRange.max)) return true;

  const price =
    item.property?.pricePerYear ||
    item.property?.price ||
    item.price ||
    item.property?.price_per_year ||
    0;

  const min = priceRange.min || 0;
  const max = priceRange.max || Infinity;
  return price >= min && price <= max;
}

function matchesBedrooms(item, bedrooms) {
  if (!bedrooms || (!bedrooms.min && !bedrooms.max)) return true;

  const count =
    item.property?.bedrooms ||
    item.bedrooms ||
    item.property?.bedroom_count ||
    0;
  const min = bedrooms.min || 0;
  const max = bedrooms.max || Infinity;
  return count >= min && count <= max;
}

function matchesBathrooms(item, bathrooms) {
  if (!bathrooms || (!bathrooms.min && !bathrooms.max)) return true;

  const count =
    item.property?.bathrooms ||
    item.bathrooms ||
    item.property?.bathroom_count ||
    0;
  const min = bathrooms.min || 0;
  const max = bathrooms.max || Infinity;
  return count >= min && count <= max;
}

function matchesSize(item, size) {
  if (!size || (!size.min && !size.max)) return true;

  const propertySize =
    item.property?.size || item.size || item.property?.property_size || 0;
  const min = size.min || 0;
  const max = size.max || Infinity;
  return propertySize >= min && propertySize <= max;
}

// function matchesAmenities(item, amenities) {
//   if (!amenities || !amenities.length) return true;

//   const propertyAmenities =
//     item.property?.amenities ||
//     item.amenities ||
//     item.property?.amenities_list ||
//     [];

//   // Convert both to comparable formats
//   const filterAmenityIds = amenities.map(amenity =>
//     typeof amenity === 'object' ? amenity.id : amenity
//   ).filter(id => id != null);

//   const itemAmenityIds = propertyAmenities.map(amenity =>
//     typeof amenity === 'object' ? amenity.id : amenity
//   ).filter(id => id != null);

//   // Check if all filter amenities exist in item amenities
//   return filterAmenityIds.every(filterId =>
//     itemAmenityIds.some(itemId => String(itemId) === String(filterId))
//   );
// }

// function matchesAmenities(item, amenities) {
//   if (!amenities || !amenities.length) return true;

//   const propertyAmenities =
//     item.property?.amenities ||
//     item.amenities ||
//     item.property?.amenities_list ||
//     [];

//   // Convert both to comparable formats
//   const filterAmenityIds = amenities
//     .map((amenity) => (typeof amenity === "object" ? amenity.id : amenity))
//     .filter((id) => id != null);

//   const itemAmenityIds = propertyAmenities
//     .map((amenity) => (typeof amenity === "object" ? amenity.id : amenity))
//     .filter((id) => id != null);

//   // Check if all filter amenities exist in item amenities
//   return filterAmenityIds.every((filterId) =>
//     itemAmenityIds.some((itemId) => String(itemId) === String(filterId))
//   );
// }

function matchesAmenities(item, amenities) {
  console.log("🔍 [DEBUG] matchesAmenities - Amenities filter:", {
    amenitiesFilter: amenities,
    itemId: item.id,
    itemTitle: item.title,
    itemPropertyAmenities:
      item.property?.amenities ||
      item.amenities ||
      item.property?.amenities_list ||
      [],
  });
  if (!amenities || !amenities.length) return true;

  const propertyAmenities =
    item.property?.amenities ||
    item.amenities ||
    item.property?.amenities_list ||
    [];

  return amenities.every((amenity) => {
    if (amenity && typeof amenity === "object" && amenity.id !== undefined) {
      return propertyAmenities.some((propAmenity) => {
        if (
          propAmenity &&
          typeof propAmenity === "object" &&
          propAmenity.id !== undefined
        ) {
          return propAmenity.id === amenity.id;
        }
        return (
          String(propAmenity) === String(amenity.id) ||
          (propAmenity.name &&
            amenity.name &&
            propAmenity.name === amenity.name)
        );
      });
    }

    if (amenity && typeof amenity === "object" && amenity.name !== undefined) {
      return propertyAmenities.some((propAmenity) => {
        if (
          propAmenity &&
          typeof propAmenity === "object" &&
          propAmenity.name !== undefined
        ) {
          return propAmenity.name === amenity.name;
        }
        return String(propAmenity) === amenity.name;
      });
    }

    return propertyAmenities.includes(amenity);
  });
}

function matchesPropertyType(item, propertyType) {
  if (!propertyType) return true;

  // Handle both propertyType and propertyTypeGroup objects
  const filterId =
    typeof propertyType === "object" ? propertyType.id : propertyType;
  const filterName =
    typeof propertyType === "object"
      ? (propertyType.name || propertyType.title || "").toLowerCase().trim()
      : "";

  // Return true if no valid filter criteria
  if (filterId === null && filterId === undefined && !filterName) return true;

  // Get the item's property type ID from all possible sources
  const itemPropertyTypeId =
    item.property?.propertyTypeId ??
    item.property?.property_type_id ??
    item.property_type_id ??
    item.group_id ??
    item.property?.group_id ??
    null;

  // Get the item's property type name from all possible sources
  const itemPropertyTypeName = (
    item.property?.propertyType ??
    item.property?.property_type ??
    item.property_type ??
    ""
  )
    .toLowerCase()
    .trim();

  // Debug logging (only for first few items to avoid spam)
  if (item.id && item.id <= 3) {
    // Only log for first 3 items
    console.log("[DEBUG] Property Type Matching:", {
      itemId: item.id,
      filterId,
      filterName,
      itemPropertyTypeId,
      itemPropertyTypeName,
      idMatch:
        itemPropertyTypeId != null &&
        String(itemPropertyTypeId) === String(filterId),
      nameMatch: filterName && itemPropertyTypeName === filterName,
    });
  }

  // First try ID matching (most reliable)
  if (filterId != null && itemPropertyTypeId != null) {
    return String(itemPropertyTypeId) === String(filterId);
  }

  // Fallback to name matching if IDs not available
  if (filterName && itemPropertyTypeName) {
    return itemPropertyTypeName === filterName;
  }

  // If we have a filter but no way to match, return false
  if (
    (filterId != null || filterName) &&
    itemPropertyTypeId == null &&
    !itemPropertyTypeName
  ) {
    return false;
  }

  // Default to true if no filter criteria can be applied
  return true;
}

function matchesFurnished(item, isFurnished) {
  if (isFurnished === null || isFurnished === undefined) {
    return true;
  }

  const itemIsFurnished =
    item.property?.isFurnished === true ||
    item.isFurnished === true ||
    item.property?.is_furnished === true ||
    item.is_furnished === true;

  return itemIsFurnished === isFurnished;
}

function matchesSubcategory(item, subcategory) {
  if (!subcategory) return true;

  const subcatId =
    typeof subcategory === "object" ? subcategory.id : subcategory;
  const itemSubcatId = item.subcategory?.id || item.sub_category_id;
  return itemSubcatId == subcatId;
}

function matchesVerified(item, verified) {
  if (!verified) return true;
  return item.verified === true;
}

function getDefaultFilters() {
  return {
    query: "",
    location: null,
    city: [],
    priceRange: { min: null, max: null },
    bedrooms: { min: null, max: null },
    bathrooms: { min: null, max: null },
    size: { min: null, max: null },
    amenities: [],
    propertyType: null,
    isFurnished: null,
    subcategory: null,
    verified: false,
    category: null,
  };
}

function normalizeFilters(filters) {
  if (!filters || typeof filters !== "object") {
    return {};
  }

  const normalized = {};

  if (filters.query !== undefined) {
    normalized.query = String(filters.query || "").trim();
  }

  if (filters.city !== undefined) {
    normalized.city = Array.isArray(filters.city) ? filters.city : [];
  }

  if (filters.priceRange !== undefined) {
    normalized.priceRange = {
      min: filters.priceRange?.min || null,
      max: filters.priceRange?.max || null,
    };
  }

  if (filters.bedrooms !== undefined) {
    normalized.bedrooms = {
      min: filters.bedrooms?.min || null,
      max: filters.bedrooms?.max || null,
    };
  }

  if (filters.bathrooms !== undefined) {
    normalized.bathrooms = {
      min: filters.bathrooms?.min || null,
      max: filters.bathrooms?.max || null,
    };
  }

  if (filters.size !== undefined) {
    normalized.size = {
      min: filters.size?.min || null,
      max: filters.size?.max || null,
    };
  }

  if (filters.amenities !== undefined) {
    normalized.amenities = Array.isArray(filters.amenities)
      ? filters.amenities
      : [];
  }

  if (filters.isFurnished !== undefined) {
    normalized.isFurnished =
      filters.isFurnished === null ? null : !!filters.isFurnished;
  }

  if (filters.verified !== undefined) {
    normalized.verified = !!filters.verified;
  }

  ["category", "subcategory", "propertyType"].forEach((key) => {
    if (filters[key] !== undefined) {
      normalized[key] = filters[key];
    }
  });

  return normalized;
}

function validateAndNormalizeFilters(filters) {
  const normalized = normalizeFilters(filters);

  if (normalized.priceRange) {
    const { min, max } = normalized.priceRange;
    if (min !== null && max !== null && min > max) {
      throw new Error("Price range minimum cannot be greater than maximum");
    }
  }

  if (normalized.bedrooms) {
    const { min, max } = normalized.bedrooms;
    if (min !== null && max !== null && min > max) {
      throw new Error("Bedroom range minimum cannot be greater than maximum");
    }
  }

  if (normalized.bathrooms) {
    const { min, max } = normalized.bathrooms;
    if (min !== null && max !== null && min > max) {
      throw new Error("Bathroom range minimum cannot be greater than maximum");
    }
  }

  return normalized;
}

function hasAnyActiveFilters(filters) {
  return (
    !!filters.query ||
    (filters.city && filters.city.length > 0) ||
    !!filters.category ||
    !!filters.subcategory ||
    !!filters.propertyType ||
    filters.isFurnished !== null ||
    filters.verified ||
    (filters.priceRange &&
      (filters.priceRange.min !== null || filters.priceRange.max !== null)) ||
    (filters.bedrooms &&
      (filters.bedrooms.min !== null || filters.bedrooms.max !== null)) ||
    (filters.bathrooms &&
      (filters.bathrooms.min !== null || filters.bathrooms.max !== null)) ||
    (filters.size &&
      (filters.size.min !== null || filters.size.max !== null)) ||
    (filters.amenities && filters.amenities.length > 0)
  );
}

function generateFilterSummary(filters) {
  const summary = [];

  if (filters.query) summary.push(`Search: "${filters.query}"`);
  if (filters.city?.length)
    summary.push(`City: ${filters.city.map((c) => c.name).join(", ")}`);
  if (filters.category) summary.push(`Category: ${filters.category.name}`);
  if (filters.subcategory) summary.push(`Type: ${filters.subcategory.title}`);
  if (filters.isFurnished !== null)
    summary.push(`Furnished: ${filters.isFurnished ? "Yes" : "No"}`);
  if (filters.verified) summary.push("Verified Only");

  return summary;
}

function normalizeAmenities(amenities) {
  if (!Array.isArray(amenities)) return [];

  return amenities.map((amenity) => {
    if (typeof amenity === "object" && amenity.name) {
      return {
        id: amenity.id || null,
        name: amenity.name,
        icon_svg: amenity.icon_svg || amenity.icon || null,
        icon: amenity.icon || "mdi-check",
      };
    }

    if (typeof amenity === "string") {
      return {
        name: amenity
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase()),
        icon_svg: null,
        icon: "mdi-check",
      };
    }

    return {
      name: String(amenity),
      icon_svg: null,
      icon: "mdi-check",
    };
  });
}
